import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { formatCompactCurrency } from '../../../utils/formatters';

// Wealth business types (Company#business_type). AUM is an RIA/wealth concept;
// p_and_c and life tenants do not have usable partial-sale AUM data.
const WEALTH_BUSINESS_TYPES = ['wealth', 'wealth_rj'];

/**
 * Whether the AUM metric should render for this listing: a wealth tenant with a
 * positive partial-sale AUM. Exported so the card can size its metrics grid
 * (3 cols with AUM, 2 without) from a single source of truth.
 */
export const shouldShowPartialSaleAum = (listing, company) =>
  WEALTH_BUSINESS_TYPES.includes(company?.business_type)
  && parseFloat(listing?.assets_under_management) > 0;

/**
 * Renders the partial sale's OWN AUM on a marketplace card, next to clients and
 * revenue. The value MUST be the listing's `assets_under_management` (the book
 * being sold) — NOT the seller's platform total (`listing.user.assets_under_management`).
 * Shown only for wealth tenants that have AUM data on the listing.
 */
const PartialSaleAumMetric = ({ listing, company }) => {
  const intl = useIntl();

  // assets_under_management is a decimal column → serialized as a string.
  if (!shouldShowPartialSaleAum(listing, company)) return null;

  return (
    <div>
      <div className="text-sm text-succession-text-secondary transition-colors duration-300">
        {intl.formatMessage({ id: 'succession.partial_sales.marketplace.aum_label' })}
      </div>
      <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
        {formatCompactCurrency(listing.assets_under_management)}
      </div>
    </div>
  );
};

PartialSaleAumMetric.propTypes = {
  listing: PropTypes.shape({
    assets_under_management: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  company: PropTypes.shape({
    business_type: PropTypes.string,
  }),
};

PartialSaleAumMetric.defaultProps = {
  company: null,
};

export default PartialSaleAumMetric;
